import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewEncapsulation,
  ViewChild,
  OnChanges,
  OnInit,
  inject,
} from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { OrganizationBackendService } from 'src/app/domain/organization/service/organization-backend.service';
import { UploadComponent } from 'src/app/domain/shared/component/upload/upload.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModalContainerComponent, SpinnerProps } from "../modal-container/modal-container.component";
import { InputComponent } from "../inputs/input-component/input.component";
import { OrganizationDTO } from "../../../organization/dto/OrganizationDTO";
import { takeUntil } from "rxjs/operators";
import { SelectorComponent } from "../inputs/selector/selector.component";
import { SelectorAutocompleteComponent } from "../inputs/selector-autocomplete/selector-autocomplete.component";
import { Countries } from '../../../../util/common/countries';
import {
  OrganizationType,
  organizationTypesMap,
  reversedOrganizationTypesMap,
} from "../../../organization/dto/organization-type";
import { ButtonComponent } from '../buttons/button.component';
import { OrganizationsService } from '../../../organization/service/organizations.service';
import { ModalDialogComponent } from "../modal-dialog/modal-dialog.component";

@Component({
  selector: 'app-modal-organization-update',
  templateUrl: './modal-organization-update.component.html',
  styleUrls: ['./modal-organization-update.component.scss'],
  providers: [],
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    ModalContainerComponent,
    InputComponent,
    UploadComponent,
    ReactiveFormsModule,
    SelectorComponent,
    SelectorAutocompleteComponent,
    ButtonComponent,
    ModalDialogComponent,
  ],
})


export class ModalOrganizationUpdateComponent implements OnDestroy, OnChanges, OnInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  private formBuilder = inject(FormBuilder);
  private orgService = inject(OrganizationBackendService);
  private orgBusinessService = inject(OrganizationsService);

  protected readonly countries = Countries;
  protected readonly Array = Array;
  protected readonly organizationTypes = organizationTypesMap;
  protected readonly reversedOrganizationTypes = reversedOrganizationTypesMap;

  protected readonly modalButtons = {
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  protected readonly spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  @Input() updatedOrganization: OrganizationDTO;
  @Output() updatedOrg = new EventEmitter<OrganizationDTO>();
  @ViewChild('upload') uploadComponent: UploadComponent;

  orgForm: FormGroup;
  showModalDialog: boolean = false;

  ngOnInit(): void {
    if (!this.orgForm && this.updatedOrganization) {
      this.createForm(this.updatedOrganization);
    }
  }

  ngOnChanges(): void {
    if (this.updatedOrganization) {
      this.createForm(this.updatedOrganization);
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async confirm(): Promise<void> {
    const awsImageName = await this.uploadComponent.upload('orgLogos', this.generateName());
    if (awsImageName) {
      this.orgForm.patchValue({ logo: awsImageName });
    }
    this.updatedOrg.emit(
      await firstValueFrom(
        this.orgService.update(this.orgForm.getRawValue())
          .pipe(takeUntil(this._destroyStream)),
      ),
    );
  }

  close(): void {
    this.updatedOrg.emit(null);
    this.ngOnDestroy();
  }

  async deleteOrg(isConfirmed: boolean): Promise<void> {
    if (isConfirmed) {
      await firstValueFrom(
        this.orgService.delete(this.orgForm.getRawValue().uuid)
          .pipe(takeUntil(this._destroyStream)),
      );
      await this.orgBusinessService.removeOrganization(this.updatedOrganization.uuid);
    }
    this.showModalDialog = false;
  }

  private generateName(): string {
    if (this.updatedOrganization.uuid) {
      const text = this.updatedOrganization.uuid;
      return text + '.jpeg';
    }
    return 'error.jpeg';
  }

  private createForm(organization: OrganizationDTO): void {
    this.orgForm = this.formBuilder.group({
      uuid: new FormControl<string>(organization.uuid),
      logo: new FormControl<string>(organization.logo || ''),
      name: new FormControl<string>(organization.name, { validators: [Validators.required] }),
      type: new FormControl<OrganizationType>(organization.type, { validators: [Validators.required] }),
      country: new FormControl<string>(organization.country, { validators: [Validators.required] }),
      webSiteLink: new FormControl<string>(organization.webSiteLink || ''),
    });
  }
}
